/**
 * Clinical–Social Coordination — member-record panel.
 *
 * The end-to-end loop for one member: clinical triggers -> auto-referrals (with
 * provenance) -> provider hand-off -> clinician loop-back -> utilization outcome,
 * plus the health observations and the clinician-gated AI insight (with a review
 * action). Backed by /api/handoff/members/{id}/coordination + /api/health/members/{id}/*.
 * Gated on the deep_integration capability.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Activity, ArrowRightCircle, CheckCircle2, Loader2, Sparkles, Stethoscope, HeartPulse, DollarSign,
} from 'lucide-react';
import { apiClient } from '@/services/apiClient';
import { useCapability } from '@/hooks/useCapabilities';

interface Props {
  memberId?: string;
}

interface Referral {
  id: number;
  referral_number?: string;
  source_type?: string;
  service_type_code?: string;
  sdoh_domain_code?: string;
  status?: string;
  urgency_level?: string;
  origin_clinical_trigger?: string | null;
  origin_icd10?: string | null;
  service_outcome?: string | null;
  pcp_notification_sent?: boolean;
  selected_provider_name?: string | null;
  service_completed_at?: string | null;
  created_at?: string;
}
interface Attribution { service_referral_id: number; pre_ed_visits: number; post_ed_visits: number; est_cost_savings: number; }
interface Insight { id: string; title: string; content: string; status: string; model?: string; created_at?: string; }
interface Observation { source_provider?: string; category?: string; display?: string; code?: string; value_num?: number | null; value_text?: string | null; unit?: string | null; effective_time?: string; }
interface Coordination {
  referrals: Referral[];
  attribution: Attribution[];
  summary: { clinically_triggered: number; looped_back: number; total_referrals: number; attributed_savings: number };
}

const money = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0);

export default function ClinicalSocialCoordinationPanel({ memberId }: Props) {
  const numId = memberId ? parseInt(memberId, 10) : NaN;
  const orgId = (() => { const c = localStorage.getItem('active_cbo_id'); return c ? parseInt(c, 10) : null; })();
  const { enabled: capEnabled, loading: capLoading } = useCapability(orgId, 'deep_integration');

  const [coord, setCoord] = useState<Coordination | null>(null);
  const [obs, setObs] = useState<Observation[]>([]);
  const [insights, setInsights] = useState<Insight[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!Number.isFinite(numId)) return;
    setLoading(true);
    try {
      const [c, o, i] = await Promise.all([
        apiClient.get(`/api/handoff/members/${numId}/coordination`).catch(() => null),
        apiClient.get(`/api/health/members/${numId}/observations`).catch(() => null),
        apiClient.get(`/api/health/members/${numId}/insights`).catch(() => null),
      ]);
      if (c?.data) setCoord(c.data);
      if (o?.data) setObs(o.data.observations || []);
      if (i?.data) setInsights(i.data.insights || []);
    } finally {
      setLoading(false);
    }
  }, [numId]);

  useEffect(() => { if (capEnabled) load(); }, [capEnabled, load]);

  const generateInsight = async () => {
    setBusy(true); setNotice(null);
    try {
      const r = await apiClient.post(`/api/health/members/${numId}/insight`);
      setNotice(`Insight generated (${r.data?.model || 'ok'}).`);
      await load();
    } catch { setNotice('Could not generate insight.'); } finally { setBusy(false); }
  };

  const review = async (id: string, action: 'approved' | 'rejected') => {
    try { await apiClient.post(`/api/health/insights/${id}/review?action=${action}`); await load(); }
    catch { setNotice('Could not update the insight.'); }
  };

  if (capLoading) return <div className="flex items-center justify-center py-10 text-ink-400"><Loader2 className="h-4 w-4 animate-spin" /></div>;
  if (!capEnabled) {
    return <div className="rounded-lg border border-v2border bg-paper px-4 py-8 text-center text-[12px] text-ink-500">Deep health data integration isn’t enabled for this organization.</div>;
  }

  const s = coord?.summary;
  const obsByCat = obs.reduce<Record<string, number>>((acc, o) => { const k = o.category || 'other'; acc[k] = (acc[k] || 0) + 1; return acc; }, {});
  const latestInsight = insights[0];

  return (
    <div className="space-y-4">
      {notice && <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-[12px] text-blue-800">{notice}</div>}

      {/* Summary */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat icon={Stethoscope} label="Clinically triggered" value={s ? String(s.clinically_triggered) : '—'} />
        <Stat icon={ArrowRightCircle} label="Total referrals" value={s ? String(s.total_referrals) : '—'} />
        <Stat icon={CheckCircle2} label="Clinician loop-back" value={s ? String(s.looped_back) : '—'} />
        <Stat icon={DollarSign} label="Attributed savings" value={s ? money(s.attributed_savings) : '—'} accent />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10 text-ink-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : (
        <>
          {/* Loop: clinical trigger -> referral -> hand-off -> loop-back */}
          <section className="overflow-hidden rounded-lg border border-v2border bg-white">
            <div className="border-b border-v2border px-4 py-2.5 text-[13px] font-semibold text-ink flex items-center gap-2">
              <ArrowRightCircle className="h-4 w-4 text-primary" /> Clinical → social referrals
            </div>
            {(coord?.referrals?.length ?? 0) === 0 ? (
              <div className="px-4 py-6 text-center text-[12px] text-ink-500">No referrals for this member yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-[12.5px]">
                  <thead><tr className="border-b border-v2border text-left text-ink-400">
                    <th className="px-4 py-2 font-medium">Trigger</th>
                    <th className="px-4 py-2 font-medium">Service</th>
                    <th className="px-4 py-2 font-medium">Status</th>
                    <th className="px-4 py-2 font-medium">Provider</th>
                    <th className="px-4 py-2 font-medium">Loop-back</th>
                  </tr></thead>
                  <tbody>
                    {coord!.referrals.map((r) => (
                      <tr key={r.id} className="border-b border-gray-100">
                        <td className="px-4 py-2">
                          {r.origin_clinical_trigger
                            ? <span className="inline-flex items-center gap-1 rounded bg-teal-50 px-1.5 py-0.5 text-teal-700"><Activity className="h-3 w-3" />{r.origin_clinical_trigger}{r.origin_icd10 ? ` · ${r.origin_icd10}` : ''}</span>
                            : <span className="text-ink-400">{r.source_type || '—'}</span>}
                        </td>
                        <td className="px-4 py-2">{r.service_type_code || '—'}{r.sdoh_domain_code ? <span className="text-ink-400"> · {r.sdoh_domain_code}</span> : ''}</td>
                        <td className="px-4 py-2"><StatusChip status={r.status} outcome={r.service_outcome} /></td>
                        <td className="px-4 py-2 text-ink-500">{r.selected_provider_name || '—'}</td>
                        <td className="px-4 py-2">{r.pcp_notification_sent ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Notified</span> : <span className="text-ink-400">Pending</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Observations + AI insight */}
          <section className="overflow-hidden rounded-lg border border-v2border bg-white">
            <div className="flex items-center justify-between gap-2 border-b border-v2border px-4 py-2.5">
              <span className="text-[13px] font-semibold text-ink flex items-center gap-2"><HeartPulse className="h-4 w-4 text-primary" /> Health data &amp; AI insight</span>
              <button onClick={generateInsight} disabled={busy} className="inline-flex items-center gap-1.5 rounded-md border border-primary/40 bg-primary/5 px-2.5 py-1 text-[12px] text-primary hover:bg-primary/10 disabled:opacity-50">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Generate insight
              </button>
            </div>
            <div className="px-4 py-3 space-y-3">
              <div className="flex flex-wrap gap-1.5">
                {Object.keys(obsByCat).length === 0
                  ? <span className="text-[12px] text-ink-500">No observations yet — connect a source in Health Connect.</span>
                  : Object.entries(obsByCat).map(([cat, n]) => <span key={cat} className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-ink-600">{cat}: {n}</span>)}
              </div>
              {latestInsight ? (
                <div className="rounded-md border border-v2border bg-paper p-3">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-[11px] uppercase tracking-wide text-ink-400">AI insight (clinician-gated) · {latestInsight.status}{latestInsight.model ? ` · ${latestInsight.model}` : ''}</span>
                    {latestInsight.status === 'pending' && (
                      <span className="flex gap-2">
                        <button onClick={() => review(latestInsight.id, 'approved')} className="rounded border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-700">Approve</button>
                        <button onClick={() => review(latestInsight.id, 'rejected')} className="rounded border border-gray-300 px-2 py-0.5 text-[11px] text-ink-600">Reject</button>
                      </span>
                    )}
                  </div>
                  <p className="whitespace-pre-wrap text-[12.5px] text-ink-700">{latestInsight.content}</p>
                </div>
              ) : (
                <p className="text-[12px] text-ink-500">No insight yet. Generate a clinician-gated summary from the connected data.</p>
              )}
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }: { icon: React.ComponentType<{ className?: string }>; label: string; value: string; accent?: boolean }) {
  return (
    <div className={`rounded-lg border p-3 ${accent ? 'border-emerald-200 bg-emerald-50' : 'border-v2border bg-white'}`}>
      <div className={`text-lg font-semibold tabular-nums ${accent ? 'text-emerald-700' : 'text-ink'}`}>{value}</div>
      <div className="mt-0.5 flex items-center gap-1 text-[11px] text-ink-400"><Icon className="h-3 w-3" /> {label}</div>
    </div>
  );
}

function StatusChip({ status, outcome }: { status?: string; outcome?: string | null }) {
  const done = status === 'completed' || status === 'closed' || outcome === 'successful';
  if (done) return <span className="rounded-full bg-green-100 px-2 py-0.5 text-[11px] text-green-700">{outcome || status}</span>;
  return <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] text-amber-700">{status || 'open'}</span>;
}
